/**
 * ScoreCard — expandable pillar card (SEO / AEO / GEO).
 * Used in both Desktop and Mobile layouts.
 * Spec §4.3, §8.3 — expand/collapse with 200ms ease-out, chevron rotates 180°.
 */

import { DrilldownRow } from './DrilldownRow';
import { ScoreTag } from './ScoreTag';
import { PillarTrajectoryHint } from './PillarTrajectoryHint';

type ScoreKind = 'seo' | 'aeo' | 'geo';

interface DeltaDescriptor {
  direction: 'improved' | 'worsened' | 'unchanged' | 'incomparable';
  display: string;
}

export interface DrilldownRowData {
  id: string;
  label: string;
  description: string;
  thenDisplay: string;
  nowDisplay: string;
  delta: DeltaDescriptor;
  /** Optional external URL — renders an external-link icon/button next to nowDisplay. */
  externalUrl?: string;
}

export interface DrilldownGroup {
  subcategory: string;
  label: string;
  rows: DrilldownRowData[];
}

interface Props {
  score: ScoreKind;
  thenValue: number | null;
  nowValue: number | null;
  delta: DeltaDescriptor;
  expanded: boolean;
  onToggle: () => void;
  drilldownGroups: DrilldownGroup[];
  confidence: 'high' | 'medium' | 'low';
}

const SCORE_LABELS: Record<ScoreKind, string> = { seo: 'SEO', aeo: 'AEO', geo: 'GEO' };

const DELTA_COLORS: Record<string, string> = {
  improved: 'text-emerald-600',
  worsened: 'text-rose-600',
  unchanged: 'text-slate-400',
  incomparable: 'text-slate-400',
};

const CONFIDENCE_LABEL: Record<string, string> = {
  high: 'High confidence',
  medium: 'Medium confidence',
  low: 'Low confidence',
};

export function ScoreCard({
  score,
  thenValue,
  nowValue,
  delta,
  expanded,
  onToggle,
  drilldownGroups,
  confidence,
}: Props) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      {/* Header / toggle */}
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={`scorecard-body-${score}`}
        onClick={onToggle}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-4">
          <span className="text-sm font-bold uppercase tracking-wider text-slate-500 w-8">
            {SCORE_LABELS[score]}
          </span>
          <div className="flex items-center gap-2">
            <ScoreTag score={thenValue} kind={score} size="sm" />
            <svg className="w-4 h-4 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <ScoreTag score={nowValue} kind={score} size="sm" />
          </div>
          <span className={`text-sm font-semibold ${DELTA_COLORS[delta.direction]}`}>
            {delta.display}
          </span>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">{CONFIDENCE_LABEL[confidence]}</span>
            <svg
              className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          {/* AFI-G3: per-pillar expected movement window */}
          <PillarTrajectoryHint pillar={score} />
        </div>
      </button>

      {/* Drilldown body — conditionally rendered with 200ms ease-out */}
      <div
        id={`scorecard-body-${score}`}
        className={`overflow-hidden transition-all duration-200 ease-out ${expanded ? 'max-h-[2000px]' : 'max-h-0'}`}
      >
        {drilldownGroups.map((group) => (
          <div key={group.subcategory} className="px-5 pb-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mt-4 mb-1">
              {group.label}
            </p>
            {/* Column header row */}
            <div className="flex items-center justify-between py-1.5 text-xs text-slate-400">
              <span>Check</span>
              <div className="flex gap-6 flex-shrink-0">
                <span className="w-12 text-right">Then</span>
                <span className="w-12 text-right">Now</span>
                <span className="w-16 text-right">Change</span>
              </div>
            </div>
            {group.rows.map((row) => (
              <DrilldownRow key={row.id} {...row} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
